import { Download, FileKey, Info, KeyRound } from "lucide-react";
import { Tabs } from "radix-ui";
import { Fragment, useState } from "react";
import { useParams } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { useGroups, useInvalidateOrg, usePolicies, useTokens } from "../api/queries";
import type { AgentPackage, EnrollmentTokenCreated, OS } from "../api/types";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { osLabel } from "../lib/status";
import { CodeBlock, LoadingRows, PageHead, Panel } from "../ui/Common";
import { Pill } from "../ui/Pills";
import { useToast } from "../ui/Toasts";
import { addMachinesMessages } from "./addMachines.i18n";

// Nomes de arquitetura como cada sistema os apresenta; iguais em todos os idiomas.
function archLabel(os: OS, arch: AgentPackage["arch"]): string {
  if (os === "macos") return arch === "arm64" ? "Apple Silicon" : "Intel";
  return arch === "arm64" ? "ARM64" : "x64";
}

export function AddMachinesPage() {
  const { orgId = "" } = useParams();
  const api = useApi();
  const toast = useToast();
  const t = useT(addMachinesMessages);
  const f = useFormat();

  function downloadBundle(r: EnrollmentTokenCreated) {
    const url = URL.createObjectURL(new Blob([r.bundle], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = r.bundleFileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  const invalidate = useInvalidateOrg();
  const groups = useGroups(orgId);
  const policies = usePolicies(orgId);
  const tokens = useTokens(orgId);

  const [label, setLabel] = useState(t.defaultLabel);
  const [groupId, setGroupId] = useState("");
  const [policyId, setPolicyId] = useState("");
  const [mode, setMode] = useState<"single" | "batch">("batch");
  const [maxUses, setMaxUses] = useState(10);
  const [hours, setHours] = useState(72);
  const [created, setCreated] = useState<EnrollmentTokenCreated | null>(null);
  const [busy, setBusy] = useState(false);
  const [now] = useState(() => Date.now());

  const g = groupId || groups.data?.[1]?.id || "";
  const p = policyId || policies.data?.[1]?.id || "";

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.createEnrollmentToken(orgId, {
        label, expiresInHours: hours, maxUses: mode === "single" ? 1 : maxUses,
        ...(g ? { groupId: g } : {}), ...(p ? { policyId: p } : {}),
      });
      setCreated(r);
      invalidate(orgId);
      toast("ok", t.created);
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : t.createFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHead
        title={t.title}
        lede={t.lede}
      />

      <div className="grid-2">
        <Panel title={t.configureTitle}>
          <form onSubmit={submit} style={{ display: "grid", gap: 16 }}>
            <div className="field">
              <label htmlFor="add-rotulo">{t.batchName}</label>
              <input id="add-rotulo" className="input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} required />
              <span className="hint">{t.batchNameHint}</span>
            </div>
            {(groups.data?.length ?? 0) > 0 && (policies.data?.length ?? 0) > 0 && (
            <div className="form-grid">
              <div className="field">
                <label htmlFor="add-grupo">{t.group}</label>
                <select id="add-grupo" className="select" value={g} onChange={(e) => setGroupId(e.target.value)}>
                  {groups.data?.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label htmlFor="add-politica">{t.policy}</label>
                <select id="add-politica" className="select" value={p} onChange={(e) => setPolicyId(e.target.value)}>
                  {policies.data?.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>
            </div>
            )}
            <div className="field">
              <span className="label" id="add-modo">{t.howMany}</span>
              <div className="segmented" role="group" aria-labelledby="add-modo">
                <button type="button" aria-pressed={mode === "single"} onClick={() => setMode("single")}>{t.single}</button>
                <button type="button" aria-pressed={mode === "batch"} onClick={() => setMode("batch")}>{t.batch}</button>
              </div>
            </div>
            <div className="form-grid">
              {mode === "batch" && (
                <div className="field">
                  <label htmlFor="add-max">{t.maxUses}</label>
                  <input id="add-max" className="input num" type="number" min={2} max={500} value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} />
                  <span className="hint">{t.maxUsesHint}</span>
                </div>
              )}
              <div className="field">
                <label htmlFor="add-validade">{t.validFor}</label>
                <select id="add-validade" className="select" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
                  {([4, 24, 72, 168, 720] as const).map((h) => <option key={h} value={h}>{t.validity[h]}</option>)}
                </select>
              </div>
            </div>
            <div className="callout">
              <Info aria-hidden="true" />
              <span>{t.securityTip}</span>
            </div>
            <div><button className="btn btn-primary" type="submit" disabled={busy}><KeyRound aria-hidden="true" /> {t.generate}</button></div>
          </form>
        </Panel>

        <Panel title={t.installTitle} sub={created ? t.installSub(created.token.label, f.dateTime(created.token.expiresAt)) : t.installSubEmpty} bodyless>
          {!created ? (
            <div className="empty">{t.installEmpty}</div>
          ) : (
            <Tabs.Root defaultValue="windows">
              <Tabs.List className="tabs-list" aria-label={t.osTabs}>
                <Tabs.Trigger value="windows">Windows</Tabs.Trigger>
                <Tabs.Trigger value="macos">macOS</Tabs.Trigger>
                <Tabs.Trigger value="linux">Linux</Tabs.Trigger>
              </Tabs.List>
              {(["windows", "macos", "linux"] as const).map((os) => {
                const inst = created.installers[os];
                const bundleButton = (primary: boolean) => (
                  <div>
                    <button className={primary ? "btn btn-primary wrap" : "btn wrap"} type="button" onClick={() => downloadBundle(created)}>
                      <FileKey aria-hidden="true" /> {t.downloadFile(created.bundleFileName)}
                    </button>
                  </div>
                );
                return (
                  <Tabs.Content key={os} value={os} className="panel-body" style={{ display: "grid", gap: 16 }}>
                    {inst.packages.length === 0 ? (
                      <>
                        {bundleButton(true)}
                        <div className="callout">
                          <Info aria-hidden="true" />
                          <span>{t.bundleTip}</span>
                        </div>
                        <div className="field">
                          <span className="label">{t.enrollCommand}</span>
                          <CodeBlock code={inst.silentCommand} label={t.commandLabel(osLabel[os])} />
                        </div>
                      </>
                    ) : (
                      <ol className="install-steps">
                        <li>
                          <strong>{t.stepAgent}</strong>
                          <div className="pkg-row">
                            {inst.packages.map((pkg, i) => (
                              <a key={pkg.fileName} className={i === 0 ? "btn btn-primary" : "btn"} href={pkg.downloadUrl} download={pkg.fileName}
                                onClick={(e) => { if (pkg.downloadUrl.startsWith("#")) { e.preventDefault(); toast("ok", t.mockDownload(pkg.fileName)); } }}>
                                <Download aria-hidden="true" /> {archLabel(os, pkg.arch)} <span className="size">{f.bytes(pkg.sizeBytes)}</span>
                              </a>
                            ))}
                          </div>
                          {inst.packages.length > 1 && <span className="hint">{t.archHint[os]}</span>}
                          <details className="checksums">
                            <summary>{t.version(inst.packages[0]!.version)} · {t.checksums}</summary>
                            <dl className="kv">
                              {inst.packages.map((pkg) => (
                                <Fragment key={pkg.fileName}><dt>{pkg.fileName}</dt><dd><code>{pkg.sha256}</code></dd></Fragment>
                              ))}
                            </dl>
                          </details>
                        </li>
                        <li>
                          <strong>{t.stepBundle}</strong>
                          {bundleButton(false)}
                          <span className="hint">{t.bundleOnce(created.token.maxUses)}</span>
                        </li>
                        <li>
                          <strong>{t.stepRun[os]}</strong>
                          <CodeBlock code={inst.silentCommand} label={t.commandLabel(osLabel[os])} />
                          <span className="hint">{t.deployHint[os]}</span>
                        </li>
                      </ol>
                    )}
                    {os === "macos" && (
                      <div className="callout warn">
                        <Info aria-hidden="true" />
                        <span>{t.macFullDisk.split("{fda}").map((part, i) => (i === 0 ? part : <span key={i}><strong>{t.macFullDiskTerm}</strong>{part}</span>))}</span>
                      </div>
                    )}
                    {inst.packages.length > 0 && os !== "macos" && (
                      <p className="ink-2" style={{ fontSize: "var(--fs-13)", margin: 0 }}>{os === "windows" ? t.windowsNote : t.linuxNote}</p>
                    )}
                    <p className="ink-3" style={{ fontSize: "var(--fs-13)", margin: 0 }}>{t.deleteBundle}</p>
                  </Tabs.Content>
                );
              })}
            </Tabs.Root>
          )}
        </Panel>
      </div>

      <Panel title={t.issuedTitle} sub={t.issuedSub} bodyless>
        {tokens.isPending ? <LoadingRows rows={3} /> : (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>{t.colBatch}</th><th>{t.colGroup}</th><th>{t.colUse}</th><th>{t.colValidity}</th><th>{t.colCreatedBy}</th></tr></thead>
              <tbody>
                {tokens.data?.map((tok) => {
                  const expired = new Date(tok.expiresAt).getTime() < now;
                  const full = tok.uses >= tok.maxUses;
                  return (
                    <tr key={tok.id}>
                      <td><strong>{tok.label}</strong></td>
                      <td className="ink-2">{groups.data?.find((x) => x.id === tok.groupId)?.name}</td>
                      <td className="num">{t.uses(tok.uses, tok.maxUses)}</td>
                      <td>{expired ? <Pill tone="idle">{t.expired(f.ago(tok.expiresAt))}</Pill> : full ? <Pill tone="idle">{t.exhausted}</Pill> : <Pill tone="ok">{t.activeUntil(f.dateTime(tok.expiresAt))}</Pill>}</td>
                      <td className="ink-2">{tok.createdBy}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
